import React, { useState, useEffect } from 'react';
import { Grid, TextField, InputLabel, Autocomplete, DialogContent, DialogActions } from '@mui/material';
import * as yup from 'yup';
import { API } from '../../../api';
import { toast } from 'react-toastify';
import { LoadingButton } from '@mui/lab';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { getUserDetails } from '../../../utils/orgName';
import useProfileDetails from '../../../hooks/useProfileDetails';
import DatePickerField from '../../../components/DatePickerField';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { popupCountActions } from '../../../redux/slices/poupsCountsSlice';
import { userActions } from '../../../redux/slices/user-profile/userProfieSlice';
import { savePosition, updatePosition, initializePosition } from '../../../redux/slices/companyPositionSlice';
import moment from 'moment';
import { companyActions } from '../../../redux/slices/companySlice'; 

const CompanyPosition = () => {
  const { ftue, settings } = useProfileDetails();
  const dispatch = useAppDispatch();
  const { userId, orgId, location } = getUserDetails();
  const [ftueLoading, setFtueLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingResponsibilities, setLoadingResponsibilities] = useState(false);
  const [responsibilityOptions, setResponsibilityOptions] = useState<string[]>([]);

  const { data: position, isInitialized } = useAppSelector((state) => state.companyPosition);
  const popuplength = useAppSelector((state) => state.popupsCount.popuplength);
  const count = useAppSelector((state) => state.popupsCount.count);

  useEffect(() => {
    if (!isInitialized) {
      dispatch(
        initializePosition({
          companyTitle: '',
          responsibilities: [],
          dateStarted: '',
          dateEnded: ''
        })
      );
    }
  }, [dispatch, isInitialized]);

  const companyPositionSchema = yup.object().shape({
    companyTitle: yup.string().when([], {
      is: () => settings.companyPosition,
      then: (schema) => schema.required('Company title is required'),
      otherwise: (schema) =>
        schema.test('companyTitle-conditional', 'Company title is required', function (value) {
          const { responsibility, dateStarted, dateEnded } = this.parent;
          const anyFieldFilled = !!value?.trim() || responsibility?.length > 0 || !!dateStarted || !!dateEnded;
          const isFilled = !!value?.trim();
          return !anyFieldFilled || isFilled;
        })
    }),
    responsibility: yup.array().when([], {
      is: () => settings.companyPosition,
      then: (schema) => schema.min(1, 'Responsibility is required').required('Responsibility is required'),
      otherwise: (schema) =>
        schema.test('responsibility-conditional', 'Responsibility is required', function (value) {
          const { companyTitle, dateStarted, dateEnded } = this.parent;
          const anyFieldFilled = !!companyTitle?.trim() || !!dateStarted || !!dateEnded || (value?.length ?? 0) > 0;
          return !anyFieldFilled || (Array.isArray(value) && value.length > 0);
        })
    }),
    dateStarted: yup.string().when([], {
      is: () => settings.companyPosition,
      then: (schema) => schema.required('Start date is required'),
      otherwise: (schema) =>
        schema.test('dateStarted-conditional', 'Start date is required', function (value) {
          const { companyTitle, responsibility, dateEnded } = this.parent;
          const anyFieldFilled = !!companyTitle?.trim() || responsibility?.length > 0 || !!dateEnded || !!value;
          return !anyFieldFilled || !!value;
        })
    }),
    dateEnded: yup.string().when([], {
      is: () => settings.companyPosition,
      then: (schema) => schema.required('End date is required')
        .test('is-after-start', 'End date must be after start date', function (value) {
          const { dateStarted } = this.parent;
          if (!value || !dateStarted) return true;
          return moment(value, 'MM/YYYY').isAfter(moment(dateStarted, 'MM/YYYY'));
        }),
      otherwise: (schema) =>
        schema.test('dateEnded-conditional', 'End date is required', function (value) {
          const { companyTitle, responsibility, dateStarted } = this.parent;
          const anyFieldFilled = !!companyTitle?.trim() || responsibility?.length > 0 || !!dateStarted || !!value;
          return !anyFieldFilled || !!value;
        }).test('is-after-start', 'End date must be after start date', function (value) {
          const { dateStarted } = this.parent;
          if (!value || !dateStarted) return true;
          return moment(value, 'MM/YYYY').isAfter(moment(dateStarted, 'MM/YYYY'));
        })
    })
  });

  const {
    handleSubmit,
    control,
    trigger,
    formState: { errors }
  } = useForm({
    resolver: yupResolver(companyPositionSchema),
    defaultValues: {
      companyTitle: position?.companyTitle,
      responsibility: position?.responsibilities,
      dateStarted: position?.dateStarted,
      dateEnded: position?.dateEnded
    }
  });

  const handleFieldChange = (field: keyof typeof position, value: any) => {
    dispatch(updatePosition({ [field]: value }));
  };

  const getResponsibilityOptions = async () => {
    try {
      const response = await API.getTagsListnew(orgId, location, 'Function/Role', 'd', false);
      return response.data.tagListResponse.map((item: any) => item.value);
    } catch (error) {
      console.error('Error fetching responsibility options:', error);
      return [];
    }
  };

  useEffect(() => {
    const fetchInitialResponsibilities = async () => {
      setLoadingResponsibilities(true);
      const options = await getResponsibilityOptions();
      setResponsibilityOptions(options);
      setLoadingResponsibilities(false);
    };
    fetchInitialResponsibilities();
  }, [orgId, location]);

  const updateFtue = async () => {
    setFtueLoading(true);
    try {
      const response = await API.updateUserFtue({ ftue: false, userId });
      if (response.status === 200) {
        dispatch(userActions.userFtueUpdate(0));
        setFtueLoading(false);
      }
    } catch (e) {
      toast.error(`Company position failed to update`);
      setFtueLoading(false);
    }
  };
  const formatDateForAPI = (dateString: string) => {
    if (!dateString) return null;
    const [month, year] = dateString.split('-');
    return `${month}/${year}`;
  };
  const onSubmit = async (formData: any) => {
    const allFieldsEmpty =
      !formData.companyTitle &&
      (!formData.responsibility || formData.responsibility.length === 0) &&
      !formData.dateStarted &&
      !formData.dateEnded;

    if (allFieldsEmpty) {
      const nextPopUpOrNot = popuplength - count;
      if (ftue && nextPopUpOrNot !== 1) {
        dispatch(popupCountActions.increment(0));
      }
      if (nextPopUpOrNot === 1) {
        dispatch(companyActions.update(true));
        await updateFtue();
      }
      return;
    }
    setIsSubmitting(true);

    try {
      const positionData = {
        companyTitle: formData.companyTitle,
        responsibilities: formData.responsibility || [],
        startDate: formatDateForAPI(formData.dateStarted) || '',
        endDate: formatDateForAPI(formData.dateEnded) || ''
      };

      const response = await API.saveCompanyPosition({ orgId, data: [positionData] });

      if (response.status === 200) {
        dispatch(
          savePosition({
            companyTitle: formData.companyTitle,
            responsibilities: formData.responsibility || [],
            dateStarted: formData.dateStarted,
            dateEnded: formData.dateEnded
          })
        );

        const nextPopUpOrNot = popuplength - count;
        if (ftue && nextPopUpOrNot !== 1) {
          dispatch(popupCountActions.increment(0));
        }
        if (nextPopUpOrNot === 1) {
          await updateFtue();
        }
      }
    } catch (error) {
      toast.error('Error saving company position');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResponsibilitySearch = async (searchValue: string) => {
    setLoadingResponsibilities(true);
    const options = await getResponsibilityOptions();
    setResponsibilityOptions(options);
    setLoadingResponsibilities(false);
  };

  // const getNextDay = (dateStr?: string) => {
  //   if (!dateStr) return '';
  //   return new Date(new Date(dateStr).getTime() + 86400000).toISOString().split('T')[0];
  // };

  return (
    <>
      <DialogContent>
        <Grid container spacing={2}>
          {/* companyTitle */}
          <Grid item xs={12}>
            <Controller
              name="companyTitle"
              control={control}
              render={({ field }) => (
                <>
                  <InputLabel>Company Title {settings.companyPosition ? '*' : ''}</InputLabel>
                  <TextField
                    {...field}
                    fullWidth
                    size="small"
                    variant="outlined"
                    placeholder="Enter title"
                    onChange={(e) => {
                      field.onChange(e);
                      handleFieldChange('companyTitle', e.target.value);
                    }}
                    onBlur={() => {
                      trigger();
                    }}
                    error={!!errors.companyTitle}
                    helperText={errors.companyTitle?.message}
                  />
                </>
              )}
            />
          </Grid>

          {/* Responsibility */}
          <Grid item xs={12}>
            <InputLabel>Responsibility {settings.companyPosition ? '*' : ''}</InputLabel>
            <Controller
              name="responsibility"
              control={control}
              render={({ field }) => (
                <Autocomplete
                  {...field}
                  multiple
                  freeSolo
                  options={responsibilityOptions}
                  loading={loadingResponsibilities}
                  onInputChange={(_, value) => handleResponsibilitySearch(value)}
                  onChange={(_, value) => {
                    field.onChange(value);
                    handleFieldChange('responsibilities', value);
                  }}
                  onBlur={() => {
                    trigger();
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      size="small"
                      variant="outlined"
                      placeholder="Select"
                      error={!!errors.responsibility}
                      helperText={errors.responsibility?.message}
                    />
                  )}
                />
              )}
            />
          </Grid>

          {/* Date started */}
          <Grid item xs={6}>
            <DatePickerField
              name="dateStarted"
              control={control}
              label="Date Started"
              required={settings?.companyPosition}
              format="MM-yyyy"
              error={errors?.dateStarted?.message}
              onChangeCallback={() => {
                trigger(['dateStarted', 'dateEnded']);
              }}
            />
          </Grid>

          {/* Date ended */}
          <Grid item xs={6}>
            <DatePickerField
              name="dateEnded"
              control={control}
              label="Date Ended"
              format="MM-yyyy"
              error={errors?.dateEnded?.message}
              onChangeCallback={() => {
                trigger(['dateStarted', 'dateEnded']);
              }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      {/* Dialog actions  */}
      <DialogActions>
        {ftue && (
          <LoadingButton
            variant="outlined"
            color="secondary"
            fullWidth
            onClick={() => dispatch(popupCountActions.decrement(0))}
          >
            Back
          </LoadingButton>
        )}
        <LoadingButton
          color="primary"
          variant="contained"
          fullWidth
          loading={isSubmitting || ftueLoading}
          onClick={handleSubmit(onSubmit)}
        >
          {popuplength - count === 1 ? 'Finish' : popuplength - count > 1 ? 'Next' : 'Save'}
        </LoadingButton>
      </DialogActions>
    </>
  );
};

export default CompanyPosition;
